import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { listPackingSessions, leaderReleaseOrder, type PackingSession } from "../dispatch/deskSessionApi";
import { formatClaimSince } from "../dispatch/deskOrderState";
import ReleaseClaimModal from "../dispatch/release-claim-modal";
import "../../styles/admin-outbound.css";

// Admin › Warehouse › Outbound › "Packing now": who holds which order at
// which desk, how far along, with a leader Release (plan 10.4). Release
// frees the order for anyone to open from its tote; the packer's boxes and
// scans are kept. A 403 / 404 / 503 (no access, old BE, tables missing)
// hides the section silently.
const PackingNow = ({ warehouse }: { warehouse: string }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<PackingSession[] | null>(null);
    const [hidden, setHidden] = useState(false);
    const [loading, setLoading] = useState(false);
    const [releasing, setReleasing] = useState<PackingSession | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await listPackingSessions(warehouse);
        setLoading(false);
        if (res.ok) {
            setRows(Array.isArray(res.data.sessions) ? res.data.sessions : []);
        } else if (res.kind === "unavailable" && res.permanent) {
            setHidden(true);
        }
        // a transient failure leaves the last list on screen
    }, [warehouse]);

    useEffect(() => { void load(); }, [load]);

    const confirmRelease = async () => {
        if (!releasing || busy) return;
        setBusy(true);
        const res = await leaderReleaseOrder(releasing.order_id);
        setBusy(false);
        // 404 not_claimed = already free (the packer put it back): same outcome.
        if (res.ok || (!res.ok && res.kind === "rejected" && res.status === 404)) {
            toast.success(t("packing_now_released"));
        } else {
            toast.error(t("packing_now_release_failed"));
        }
        setReleasing(null);
        await load();
    };

    if (hidden || rows === null) return null;

    return (
        <div className="packing-now">
            <div className="outbound-toolbar">
                <h2 className="unload-summary-heading" style={{ margin: 0 }}>{t("packing_now_title")}</h2>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => void load()} disabled={loading} aria-label={t("refresh")}>
                    <RefreshCw size={14} className={loading ? "unload-spin" : undefined} />
                    {t("refresh")}
                </button>
            </div>
            <p className="admin-settings-section-hint">{t("packing_now_hint")}</p>
            {rows.length === 0 ? (
                <p className="unload-summary-empty">{t("packing_now_empty")}</p>
            ) : (
                <table className="unload-summary-table" aria-label={t("packing_now_title")}>
                    <thead>
                        <tr>
                            <th>{t("packing_now_col_packer")}</th>
                            <th>{t("packing_now_col_desk")}</th>
                            <th>{t("packing_now_col_order")}</th>
                            <th>{t("packing_now_col_since")}</th>
                            <th>{t("packing_now_col_units")}</th>
                            <th>{t("packing_now_col_boxes")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.order_id}>
                                <td>{r.user_name ?? "—"}</td>
                                <td>{r.desk_id ?? "—"}</td>
                                <td><strong>{r.order_number ?? r.order_id}</strong></td>
                                <td className="unload-summary-muted">{formatClaimSince(r.order_opened_at) || "—"}</td>
                                <td>{r.units_done} / {r.units_total}</td>
                                <td>{r.boxes}</td>
                                <td>
                                    <button
                                        type="button"
                                        className="btn btn-secondary dispatch-modal-btn"
                                        onClick={() => setReleasing(r)}
                                    >
                                        {t("packing_now_release")}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {releasing && (
                <ReleaseClaimModal
                    orderNumber={releasing.order_number ?? releasing.order_id}
                    packerName={releasing.user_name ?? t("desk_claim_unknown_packer")}
                    busy={busy}
                    onConfirm={confirmRelease}
                    onCancel={() => setReleasing(null)}
                />
            )}
        </div>
    );
};

export default PackingNow;
